/**
 * 소셜 로그인으로 나갔다 돌아오는 길 (D108 · 히어로챗 D127 · D131 판).
 *
 * - 웹: 이 창이 제공자 화면으로 갔다가 /auth/done?ticket=… 으로 돌아옵니다
 * - 앱: 제공자 화면을 **앱 안 브라우저**(iOS SFSafariViewController · 안드로이드 Custom Tabs, @capacitor/browser)로 엽니다.
 *   웹뷰에서 주소를 바꾸면 Capacitor 가 시스템 브라우저로 넘겨 앱이 사라지고, iOS 는 그것으로 심사에서 반려됩니다
 *   (귀인도감 Guideline 4). 구글은 웹뷰 안 로그인을 아예 막습니다(disallowed_useragent).
 *   돌아올 때는 서버의 다리 페이지가 insaeng://auth/done?ticket=… 로 앱을 부르고, MainActivity · AppDelegate 가
 *   웹뷰에 /auth/done?ticket=… 를 엽니다. 그 화면(AuthDonePage)이 앱 안 브라우저를 닫고 표를 세션으로 바꿉니다
 */
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { API_BASE } from '../api/client.js';

/** 'web' · 'android' · 'ios' */
export function loginPlatform() {
    if (!Capacitor.isNativePlatform()) {
        return 'web';
    }
    const platform = Capacitor.getPlatform();
    return platform === 'ios' || platform === 'android' ? platform : 'web';
}

/** 제공자로 보내는 서버 주소(앱 안 브라우저는 웹뷰 밖이라 절대 주소여야 함) */
export function loginStartUrl(provider, platform = loginPlatform(), origin = window.location.origin) {
    const path = `${API_BASE.replace(/\/+$/, '')}/auth/${encodeURIComponent(provider)}?platform=${platform}`;
    return new URL(path, origin).href;
}

let browserOpen = false;
let closedByUs = false;

/**
 * 로그인을 시작합니다.
 * @returns {Promise<void>}
 */
export async function startSocialLogin(provider) {
    const platform = loginPlatform();
    const url = loginStartUrl(provider, platform);
    if (platform === 'web') {
        window.location.assign(url);
        return;
    }
    try {
        await Browser.open({ url, presentationStyle: 'fullscreen', toolbarColor: '#F6F1E7' });
        browserOpen = true;
        closedByUs = false;
    } catch (error) {
        // 플러그인이 없는 옛 껍데기: 웹뷰가 시스템 브라우저로 넘기는 길로 갑니다
        console.error('[social] 앱 안 브라우저를 열지 못함', error?.message ?? error);
        window.location.assign(url);
    }
}

/** 돌아왔으면 앱 안 브라우저를 닫습니다(iOS 는 앱이 불려도 저절로 닫히지 않음). 열린 것이 없으면 아무 일도 없음 */
export async function closeLoginBrowser() {
    if (loginPlatform() === 'web') {
        return;
    }
    if (browserOpen) {
        closedByUs = true;
    }
    try {
        await Browser.close();
    } catch {
        // iOS 는 열린 것이 없으면 거절합니다. 닫을 것이 없었던 것입니다
        closedByUs = false;
    }
}

/**
 * 사람이 앱 안 브라우저를 그냥 닫았을 때 fn() 을 부릅니다(로그인 중 버튼을 되살리려고).
 * iOS 의 「완료」는 앱을 떠났다 오는 것이 아니라 visibilitychange 로 잡히지 않습니다(귀인도감에서 버튼이 굳음).
 * @returns {() => void} 그만 듣기
 */
export function onLoginBrowserAbandoned(fn) {
    if (loginPlatform() === 'web') {
        return () => {};
    }
    let handle = null;
    try {
        handle = Browser.addListener('browserFinished', () => {
            const abandoned = !closedByUs;
            browserOpen = false;
            closedByUs = false;
            if (abandoned) {
                fn();
            }
        });
    } catch {
        return () => {};
    }
    return () => {
        Promise.resolve(handle)
            .then((listener) => listener?.remove?.())
            .catch(() => {});
    };
}
